import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  sendSignInLinkToEmail,
  linkWithCredential,
  EmailAuthProvider,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword,
  signInWithCustomToken,
} from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import {
  getDatabase,
  ref as refDatabase,
  get,
  child,
  onValue,
  orderByChild,
  equalTo,
  query,
  set,
} from "firebase/database";
import {
  getFirestore,
  collection,
  doc,
  query as queryFireStore,
  where,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { isIOS, isNotificationSupported } from "./validations";

const firebaseConfig =
  process.env.NODE_ENV === "development" ||
  window?.location?.host?.includes?.("test.")
    ? {
        apiKey: "AIzaSyBr0FnuSJZ1eD1Fs4SrsjWeLxwYPn7qkaQ",
        authDomain: "froin-51efe.firebaseapp.com",
        databaseURL: "https://froin-51efe-default-rtdb.firebaseio.com",
        projectId: "froin-51efe",
        storageBucket: "froin-51efe.appspot.com",
        messagingSenderId: "975732036410",
        appId: "1:975732036410:web:62a10df506eeba3521f603",
        measurementId: "G-CDSYMGGDYJ",
      }
    : {
        apiKey: "AIzaSyDrbzGr_ydgA-HxW7QC3PM77GwsbzBDAe8",
        authDomain: "froin-club.firebaseapp.com",
        databaseURL: "https://froin-club-default-rtdb.firebaseio.com",
        projectId: "froin-club",
        storageBucket: "froin-club.appspot.com",
        messagingSenderId: "785344433615",
        appId: "1:785344433615:web:130fc664e1732d2ec9d6ba",
        measurementId: "G-TJSPNKRV1D",
      };

const messagingToken =
  process.env.NODE_ENV === "development" ||
  window?.location?.host?.includes?.("test.")
    ? "BJUJ8wGDaQ-KjDu5-8Iir8lmwb6V0RqZE_ycL32uDMFcxYsBINv18p7QqoLj5nfAAGv-B79IAPcxv48avndeTR0"
    : "BB_Hxifb6ZmI8XjxNY5gNv3rbQw8tZbJ6QKmzTo5h5BlPLffK-SS9XwUluTH5eJIwKNQhonwneAym3v8gZFisDM";

const app = initializeApp(firebaseConfig);

// Analytics
const analytics = getAnalytics(app);
const analyticsService = {
  logEvent: (eventName: string, params) =>
    logEvent(analytics, eventName, params),
};

// Authentication
const auth = getAuth(app);
const actionCodeSettings = {
  url: window.location.href,
  handleCodeInApp: true,
};
const authService = {
  EmailAuthProvider: EmailAuthProvider,
  sendSignInLinkToEmail: (email: string) =>
    sendSignInLinkToEmail(auth, email, actionCodeSettings),
  onAuthStateChanged: (observer) => onAuthStateChanged(auth, observer),
  signOut: () => signOut(auth),
  currentUser: () => getAuth(app).currentUser,
  linkWithCredential: (credential) =>
    linkWithCredential(auth.currentUser, credential),
  isSignInWithEmailLink: (url: string) => isSignInWithEmailLink(auth, url),
  signInWithEmailLink: (email: string, url: string) =>
    signInWithEmailLink(auth, email, url),
  signInWithEmailAndPassword: (email: string, password: string) =>
    signInWithEmailAndPassword(auth, email, password),
  signInWithCustomToken: (token: string) => signInWithCustomToken(auth, token),
};

// Firestore
const firestore = getFirestore(app);
const firestoreService = {
  setDoc: (refCollection: string, refDocument: string, data) =>
    setDoc(doc(firestore, refCollection, refDocument), data),
  updateDoc: (refCollection: string, refDocument: string, data) =>
    updateDoc(doc(firestore, refCollection, refDocument), data),
  getDoc: (refCollection: string, refDocument: string) =>
    getDoc(doc(firestore, refCollection, refDocument)),
  getCollection: (refCollection: string) =>
    getDocs(collection(firestore, refCollection)),
  getQuery: (refCollection: string, queries = []) => {
    const returnQueries = () =>
      queries.map((queryData) =>
        where(queryData.label, queryData.condition, queryData.value)
      );
    return getDocs(
      queryFireStore(collection(firestore, refCollection), ...returnQueries())
    );
  },
};

// Realt time Databse
const database = getDatabase(app);
const databaseService = {
  getDatabase: (url: string) => {
    const dbRef = refDatabase(database);
    return get(child(dbRef, url));
  },
  onDatabase: (url: string, callback) => {
    const dbRef = refDatabase(database);
    const listener = onValue(
      child(dbRef, url),
      (snapshot) => {
        callback(snapshot.val());
      },
      (snapshot) => {
        console.log("Error onDatabase: ", snapshot);
      }
    );

    return listener;
  },
  getQueryDatabase: (url: string, byChild, equalValue) => {
    const dbRef = refDatabase(database, url);
    return get(query(dbRef, orderByChild(byChild), equalTo(equalValue)));
  },
  saveDatabase: (url: string, data) => {
    const dbRef = refDatabase(database);
    return set(child(dbRef, url), data);
  },
};

// Push Notifications

const messaging = async () => {
  try {
    const isSupportedBrowser = await isSupported();
    if (isSupportedBrowser) {
      return getMessaging(app);
    }
    console.log("Firebase not supported this browser");
    return null;
  } catch (err) {
    console.log(err);
    return null;
  }
};

const messagingService = {
  getToken: async () => {
    try {
      if (isIOS() || !isNotificationSupported()) return;

      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        const messagingData = await messaging();
        const currentToken = await getToken(messagingData, {
          vapidKey: messagingToken,
        });
        if (currentToken) {
          return currentToken;
        } else {
          return Notification.requestPermission();
        }
      }
    } catch (error) {
      throw new Error(error);
    }
  },
  onMessage: async (callback) => {
    try {
      const messagingData = await messaging();
      const payload = new Promise((resolve) => {
        onMessage(messagingData, (payloadServer) => {
          resolve(payloadServer);
          callback?.(payloadServer);
        });
      });

      return payload;
    } catch (error) {
      throw new Error(error);
    }
  },
};

export {
  authService,
  analyticsService,
  databaseService,
  firestoreService,
  messagingService,
};
